import { useState, useEffect, Suspense, lazy } from "react";
import { supabase } from "./supabase";
import LoginScreen from "./LoginScreen";
import { track, identify } from "./analytics.js";
import { trackSessionStart } from "./lib/retentionTracker.js";
import { trackGmailConnected } from "./lib/trustScoreEvents.js";
import ProfileSelect from "./components/ProfileSelect.jsx";
import ResetPassword from "./components/ResetPassword.jsx";
import PrivacyPolicy from "./components/PrivacyPolicy.jsx";
import Terms from "./pages/Terms.jsx";
import PoweredByClaude from "./pages/PoweredByClaude.jsx";


const OnboardingFlow = lazy(() => import("./components/OnboardingFlow.jsx"));
const Onboarding = lazy(() => import("./components/Onboarding.jsx"));
const DirectedRevelation = lazy(() => import("./components/onboarding/DirectedRevelation.jsx"));
const Dashboard = lazy(() => import("./components/Dashboard.jsx"));
const LLCCockpit = lazy(() => import("./pages/LLCCockpit.jsx"));
const Opportunities = lazy(() => import("./pages/Opportunities.jsx"));
const DecisionCanvas = lazy(() => import("./pages/DecisionCanvas.jsx"));
const Settings = lazy(() => import("./pages/Settings.jsx"));
const IntelligenceBoard = lazy(() => import("./pages/IntelligenceBoard.jsx"));
const Vault = lazy(() => import("./pages/Vault.jsx"));
const VaultPortfolio = lazy(() => import("./pages/VaultPortfolio.jsx"));
const HouseholdOps = lazy(() => import("./components/HouseholdOps.jsx"));
const Swarm = lazy(() => import("./pages/Swarm.jsx"));
const Clarify = lazy(() => import("./pages/Clarify.jsx"));
const Policies = lazy(() => import("./pages/Policies.jsx"));
const AdminCodes = lazy(() => import("./pages/AdminCodes.jsx"));
const FridgeBoard = lazy(() => import('./components/FridgeBoard'));
const SharedInsight = lazy(() => import('./pages/SharedInsight'));
const MemoryUnderstanding = lazy(() => import('./pages/MemoryUnderstanding'));

export default function App() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  // GAP-021: profile gate state. DB is authoritative.
  const [profileChecked, setProfileChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [stage,setStage]=useState(()=>{try{const s=localStorage.getItem("aria_stage");return s==="pin"?"profile":s||"profile";}catch{return "profile";}});
  const [profileId,setProfileId]=useState(()=>{try{return localStorage.getItem("aria_profile")||null;}catch{return null;}});
  const [pendingGmailToken,setPendingGmailToken]=useState(null);
  const [passwordRecovery,setPasswordRecovery]=useState(false);

  // GAP-021 fix: DB-authoritative onboarding gate.
  // localStorage is a WRITE-ONLY perf cache, never read for routing.
  // Removed early-return on localStorage so DB can correct stale cache (cross-device).
  function syncOnboardingState(userId) {
    setProfileChecked(false);
    supabase.from("profiles").select("onboarding_complete").eq("id", userId).single()
      .then(({ data }) => {
        const complete = !!data?.onboarding_complete;
        setNeedsOnboarding(!complete);
        setProfileChecked(true);
        try {
          if (complete) localStorage.setItem("aria_onboarded", "true");
          else localStorage.removeItem("aria_onboarded");
        } catch {}
      });
  }

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session:s}})=>{
      setSession(s);
      setAuthReady(true);
      if (s?.user) {
        identify(s.user.id);
        track("user_login");
        trackSessionStart();
        syncOnboardingState(s.user.id);
      } else {
        // No session → nothing to gate on; mark check satisfied so static + login render.
        setProfileChecked(true);
      }
    });
    const {data:{subscription}} = supabase.auth.onAuthStateChange((event,s)=>{
      setSession(s);
      if (event === 'PASSWORD_RECOVERY') { setPasswordRecovery(true); return; }
      if (s?.user) {
        identify(s.user.id);
        if (event === 'SIGNED_IN') track("user_login");
        trackSessionStart();
        syncOnboardingState(s.user.id);
      } else {
        setProfileChecked(true);
        setNeedsOnboarding(false);
      }
    });
    return ()=>subscription.unsubscribe();
  },[]);

  // D-594: Intercept Plaid OAuth redirect at root → /settings
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_state_id')) {
      window.location.replace('/settings' + window.location.search);
    }
  }, []);

  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const gmailToken=params.get("gmail_token");
    const gmailError=params.get("gmail_error");
    if(gmailToken||gmailError){
      window.history.replaceState({},"",window.location.pathname);
    }
    if(gmailToken){
      // If we landed on /settings from OAuth callback, stay there — don't override stage.
      // The token is stored in DB by the callback; pendingGmailToken is for InvoiceCapture scan.
      if(window.location.pathname!=="/settings"){
        const urlStage=params.get("stage")||"dashboard";
        const urlProfile=params.get("profile")||"both";
        try{localStorage.setItem("aria_stage",urlStage);}catch{}setStage(urlStage);
        try{localStorage.setItem("aria_profile",urlProfile);}catch{}setProfileId(urlProfile);
        try{localStorage.setItem("aria_tab","llc");}catch{}
      }
      setPendingGmailToken(gmailToken);trackGmailConnected();
    }
  },[]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setStage("profile");
    setProfileId(null);
    setProfileChecked(false);
    setNeedsOnboarding(false);
    try { localStorage.removeItem("aria_onboarded"); } catch {}
  };

  const handleOnboardingComplete = () => {
    setStage("profile");
  };

  const fallback = <div style={{minHeight:"100vh",background:"#0A0E14",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"IBM Plex Mono,monospace"}}><div style={{color:"#9ca3af",fontSize:12}}>Loading...</div></div>;

  // Static pages - no auth required
  if (window.location.pathname === "/privacy") return <PrivacyPolicy />;
  if (window.location.pathname === "/terms") return <Terms />;
  if (window.location.pathname === "/powered-by-claude") return <PoweredByClaude />;
  if (window.location.pathname === "/swarm") return <Suspense fallback={fallback}><Swarm /></Suspense>;
  if (window.location.pathname.startsWith("/invite/")) return <Suspense fallback={fallback}><SharedInsight /></Suspense>;

  if (!authReady) return fallback;
  if (passwordRecovery && session) return <ResetPassword onComplete={()=>{setPasswordRecovery(false);setStage("profile");}}/>;
  if (!session) return <LoginScreen onAuth={s=>setSession(s)} />;
  // GAP-021: hold ALL post-login routing until DB profile check resolves.
  // Prevents incognito/fresh-session race where empty localStorage caused wrong route.
  if (!profileChecked) return fallback;
  // Explicit /onboarding route — Directed Revelation Loop (D-272 / D-620)
  if (window.location.pathname === "/onboarding") return <Suspense fallback={fallback}><DirectedRevelation session={session} /></Suspense>;
  if (window.location.pathname === "/clarify") return <Suspense fallback={fallback}><Clarify session={session} /></Suspense>;
  if (window.location.pathname === "/policies") return <Suspense fallback={fallback}><Policies session={session} /></Suspense>;
  if (window.location.pathname === "/llc") return <Suspense fallback={fallback}><LLCCockpit session={session} /></Suspense>;
  if (window.location.pathname === "/opportunities") return <Suspense fallback={fallback}><Opportunities session={session} /></Suspense>;
  if (window.location.pathname === "/canvas") return <Suspense fallback={fallback}><DecisionCanvas session={session} /></Suspense>;
  if (window.location.pathname === "/settings/understanding") return <Suspense fallback={fallback}><MemoryUnderstanding session={session} /></Suspense>;
  if (window.location.pathname === "/settings") return <Suspense fallback={fallback}><Settings session={session} /></Suspense>;
  if (window.location.pathname === "/intelligence") return <Suspense fallback={fallback}><IntelligenceBoard session={session} /></Suspense>;
  if (window.location.pathname === "/vault/portfolio") return <Suspense fallback={fallback}><VaultPortfolio session={session} /></Suspense>;
  if (window.location.pathname === "/vault") return <Suspense fallback={fallback}><Vault session={session} /></Suspense>;
  if (window.location.pathname === "/ops") return <Suspense fallback={fallback}><HouseholdOps session={session} /></Suspense>;
  if (window.location.pathname === "/admin/codes") return <Suspense fallback={fallback}><AdminCodes session={session} /></Suspense>;
  if (window.location.pathname === "/board") return <Suspense fallback={fallback}><FridgeBoard session={session} /></Suspense>;
  if (window.location.pathname.startsWith("/share/")) return <Suspense fallback={fallback}><SharedInsight /></Suspense>;
  // D-620: Onboarding gate now routes to DirectedRevelation (was <Onboarding>).
  // needsOnboarding is DB-derived state, not localStorage read.
  if (needsOnboarding && stage !== "dashboard") return <Suspense fallback={fallback}><DirectedRevelation session={session} /></Suspense>;
  if (stage==="profile") return <ProfileSelect onSelect={id=>{setProfileId(id);setStage("dashboard");}}/>;
  return <><a href="#main-content" className="skip-to-content">Skip to main content</a><Suspense fallback={fallback}><Dashboard profileId={profileId} session={session} onSwitch={()=>setStage("profile")} onLogout={handleLogout} pendingGmailToken={pendingGmailToken} onGmailTokenUsed={()=>setPendingGmailToken(null)}/></Suspense></>;
}
